import { Input } from '@/components/common/Input';
import { CountrySelect, Select } from '@/components/form';
import { todayIso } from '../format';
import { EmailInput, PhoneInput, UrlInput } from '../inputs';
import { useLookup } from '@/hooks/useLookups';
import type { DraftCompanyDetails } from './types';

const DATE_INPUT =
  'w-full rounded-lg border border-[#e0e0e0] bg-white px-4 py-2.5 text-sm text-[#333] outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]';

interface Props {
  value: DraftCompanyDetails;
  onChange: (next: DraftCompanyDetails) => void;
  errors: Record<string, string>;
}

const FIELD_ROW = 'grid grid-cols-1 gap-4 md:grid-cols-2';
const LABEL = 'mb-1 block text-xs font-medium text-[#555]';

export function CompanyDetailsPanel({ value, onChange, errors }: Props) {
  const { items: legalForms, loading: legalFormsLoading } = useLookup('legal-forms');
  const { items: businessSectors, loading: businessSectorsLoading } = useLookup('business-sectors');
  const { items: businessActivities, loading: businessActivitiesLoading } =
    useLookup('business-activities');

  const legalFormOptions = legalForms.map((it) => ({ value: String(it.id), label: it.label }));
  const businessSectorOptions = businessSectors.map((it) => ({
    value: String(it.id),
    label: it.label,
  }));
  const businessActivityOptions = businessActivities.map((it) => ({
    value: String(it.id),
    label: it.label,
  }));

  const set = <K extends keyof DraftCompanyDetails>(key: K, v: DraftCompanyDetails[K]) =>
    onChange({ ...value, [key]: v });

  const setAddress = <K extends keyof DraftCompanyDetails['registeredAddress']>(
    key: K,
    v: DraftCompanyDetails['registeredAddress'][K],
  ) =>
    onChange({
      ...value,
      registeredAddress: { ...value.registeredAddress, [key]: v },
    });

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#333]">Company information</h3>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Company name *</label>
            <Input
              value={value.companyName}
              onChange={(v) => set('companyName', v)}
              error={errors['companyDetails.companyName']}
              placeholder="Acme SARL"
            />
          </div>
          <div>
            <label className={LABEL}>Country of incorporation *</label>
            <CountrySelect
              value={value.countryCode}
              onChange={(v) => set('countryCode', v)}
              error={errors['companyDetails.countryCode']}
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Legal form</label>
            <Select
              value={value.legalFormId != null ? String(value.legalFormId) : ''}
              onChange={(v) => set('legalFormId', v ? Number(v) : undefined)}
              options={legalFormOptions}
              placeholder={legalFormsLoading ? 'Loading…' : 'Select legal form…'}
              disabled={legalFormsLoading}
            />
          </div>
          <div>
            <label className={LABEL}>Registration number</label>
            <Input
              value={value.incorporationNumber ?? ''}
              onChange={(v) => set('incorporationNumber', v)}
              placeholder="SIREN / SIRET"
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Business sector</label>
            <Select
              value={value.businessSectorId != null ? String(value.businessSectorId) : ''}
              onChange={(v) => set('businessSectorId', v ? Number(v) : undefined)}
              options={businessSectorOptions}
              placeholder={businessSectorsLoading ? 'Loading…' : 'Select sector…'}
              disabled={businessSectorsLoading}
            />
          </div>
          <div>
            <label className={LABEL}>Business activity</label>
            <Select
              value={value.businessActivityId != null ? String(value.businessActivityId) : ''}
              onChange={(v) => set('businessActivityId', v ? Number(v) : undefined)}
              options={businessActivityOptions}
              placeholder={businessActivitiesLoading ? 'Loading…' : 'Select activity…'}
              disabled={businessActivitiesLoading}
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Tax ID (TIN)</label>
            <Input value={value.tin ?? ''} onChange={(v) => set('tin', v)} />
          </div>
          <div>
            <label className={LABEL}>Website</label>
            <UrlInput
              value={value.website ?? ''}
              onChange={(v) => set('website', v || undefined)}
              placeholder="https://acme.com"
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Contact email</label>
            <EmailInput
              value={value.contactEmail ?? ''}
              onChange={(v) => set('contactEmail', v || undefined)}
            />
          </div>
          <div>
            <label className={LABEL}>Telephone</label>
            <PhoneInput
              value={value.telephone ?? ''}
              onChange={(v) => set('telephone', v || undefined)}
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>Incorporation date</label>
            <input
              type="date"
              value={value.incorporationDate ?? ''}
              onChange={(e) => set('incorporationDate', e.target.value || undefined)}
              max={todayIso()}
              className={DATE_INPUT}
            />
          </div>
          <div />
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-[#333]">Registered address</h3>
        <div>
          <label className={LABEL}>Line 1 *</label>
          <Input
            value={value.registeredAddress.line1}
            onChange={(v) => setAddress('line1', v)}
            error={errors['companyDetails.registeredAddress.line1']}
          />
        </div>
        <div>
          <label className={LABEL}>Line 2</label>
          <Input
            value={value.registeredAddress.line2 ?? ''}
            onChange={(v) => setAddress('line2', v)}
          />
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>City *</label>
            <Input
              value={value.registeredAddress.city}
              onChange={(v) => setAddress('city', v)}
              error={errors['companyDetails.registeredAddress.city']}
            />
          </div>
          <div>
            <label className={LABEL}>State / region</label>
            <Input
              value={value.registeredAddress.state ?? ''}
              onChange={(v) => setAddress('state', v)}
            />
          </div>
        </div>
        <div className={FIELD_ROW}>
          <div>
            <label className={LABEL}>ZIP *</label>
            <Input
              value={value.registeredAddress.zip}
              onChange={(v) => setAddress('zip', v)}
              error={errors['companyDetails.registeredAddress.zip']}
            />
          </div>
          <div>
            <label className={LABEL}>Country *</label>
            <CountrySelect
              value={value.registeredAddress.countryCode}
              onChange={(v) => setAddress('countryCode', v)}
              error={errors['companyDetails.registeredAddress.countryCode']}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export function isCompanyDetailsValid(v: DraftCompanyDetails): boolean {
  return Boolean(
    v.companyName.trim() &&
    v.countryCode.trim().length === 2 &&
    v.registeredAddress.line1.trim() &&
    v.registeredAddress.city.trim() &&
    v.registeredAddress.zip.trim() &&
    v.registeredAddress.countryCode.trim().length === 2,
  );
}
